import { z } from "zod";
import { IdempotencyKeySchema, IsoDateTimeSchema } from "./primitives.js";
const DemoNoteTemplateIdSchema = z.string().trim().min(1).max(100);
const ShortTextSchema = z.string().trim().min(1).max(240);
const OptionalTextSchema = z.string().trim().max(500);
const FieldValueTextSchema = z.string().trim().min(1).max(2_000);
export const DemoNoteTemplateCategorySchema = z.enum([
    "encounter",
    "letter",
    "referral",
    "patientInstruction",
]);
export const DemoNoteTemplateSoapTargetSchema = z.enum([
    "subjective",
    "objective",
    "assessment",
    "plan",
]);
export const DemoNoteTemplateFieldTypeSchema = z.enum([
    "shortText",
    "longText",
    "dropdown",
    "checkboxes",
    "radio",
]);
export const DemoNoteTemplateStatusSchema = z.enum(["active", "archived"]);
const choiceFieldTypes = new Set([
    "dropdown",
    "checkboxes",
    "radio",
]);
export const DemoNoteTemplateFieldSchema = z
    .object({
    id: DemoNoteTemplateIdSchema,
    label: ShortTextSchema,
    type: DemoNoteTemplateFieldTypeSchema,
    required: z.boolean(),
    options: z.array(z.string().trim().min(1).max(120)).max(20).default([]),
    defaultValues: z.array(FieldValueTextSchema).max(20).default([]),
})
    .strict()
    .superRefine((value, context) => {
    if (choiceFieldTypes.has(value.type)) {
        if (value.options.length === 0) {
            context.addIssue({
                code: "custom",
                message: "Choice fields need at least one option",
                path: ["options"],
            });
        }
        if (new Set(value.options).size !== value.options.length) {
            context.addIssue({
                code: "custom",
                message: "Field options must be unique",
                path: ["options"],
            });
        }
        value.defaultValues.forEach((defaultValue, index) => {
            if (!value.options.includes(defaultValue)) {
                context.addIssue({
                    code: "custom",
                    message: "Default values must match one of the field options",
                    path: ["defaultValues", index],
                });
            }
        });
        if (new Set(value.defaultValues).size !== value.defaultValues.length) {
            context.addIssue({
                code: "custom",
                message: "Default values must be unique",
                path: ["defaultValues"],
            });
        }
    }
    else if (value.options.length > 0) {
        context.addIssue({
            code: "custom",
            message: "Text fields cannot define options",
            path: ["options"],
        });
    }
    if (value.type !== "checkboxes" && value.defaultValues.length > 1) {
        context.addIssue({
            code: "custom",
            message: "Only checkbox fields may define multiple default values",
            path: ["defaultValues"],
        });
    }
});
export const DemoNoteTemplateSectionSchema = z
    .object({
    id: DemoNoteTemplateIdSchema,
    title: ShortTextSchema,
    // Only encounter template sections write into a SOAP target; the template schema
    // cross-validates the pairing so document categories stay target-free.
    soapTarget: DemoNoteTemplateSoapTargetSchema.optional(),
    fields: z.array(DemoNoteTemplateFieldSchema).max(30),
})
    .strict()
    .superRefine((value, context) => {
    const fieldIds = value.fields.map((field) => field.id);
    if (new Set(fieldIds).size !== fieldIds.length) {
        context.addIssue({
            code: "custom",
            message: "Field identifiers must be unique within a section",
            path: ["fields"],
        });
    }
});
export const DemoNoteTemplateSchema = z
    .object({
    id: DemoNoteTemplateIdSchema,
    name: ShortTextSchema,
    description: OptionalTextSchema,
    category: DemoNoteTemplateCategorySchema,
    status: DemoNoteTemplateStatusSchema,
    version: z.number().int().positive(),
    sections: z.array(DemoNoteTemplateSectionSchema).min(1).max(20),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    const sectionIds = value.sections.map((section) => section.id);
    if (new Set(sectionIds).size !== sectionIds.length) {
        context.addIssue({
            code: "custom",
            message: "Section identifiers must be unique within a template",
            path: ["sections"],
        });
    }
    const fieldIds = value.sections.flatMap((section) => section.fields.map((field) => field.id));
    if (new Set(fieldIds).size !== fieldIds.length) {
        context.addIssue({
            code: "custom",
            message: "Field identifiers must be unique within a template",
            path: ["sections"],
        });
    }
    value.sections.forEach((section, index) => {
        if (value.category === "encounter" && section.soapTarget === undefined) {
            context.addIssue({
                code: "custom",
                message: "Encounter template sections must write into a SOAP target",
                path: ["sections", index, "soapTarget"],
            });
        }
        if (value.category !== "encounter" && section.soapTarget !== undefined) {
            context.addIssue({
                code: "custom",
                message: "Only encounter template sections may target a SOAP field",
                path: ["sections", index, "soapTarget"],
            });
        }
    });
});
export const DemoNoteTemplateVisitTypeDefaultSchema = z
    .object({
    appointmentTypeId: DemoNoteTemplateIdSchema,
    templateId: DemoNoteTemplateIdSchema,
})
    .strict();
export const DemoNoteTemplateDefaultsSchema = z
    .object({
    version: z.number().int().positive(),
    defaults: z.array(DemoNoteTemplateVisitTypeDefaultSchema).max(100),
})
    .strict()
    .superRefine((value, context) => {
    const appointmentTypeIds = value.defaults.map((entry) => entry.appointmentTypeId);
    if (new Set(appointmentTypeIds).size !== appointmentTypeIds.length) {
        context.addIssue({
            code: "custom",
            message: "Each visit type may map to at most one default template",
            path: ["defaults"],
        });
    }
});
export const DemoNoteTemplatesStateSchema = z
    .object({
    templates: z.array(DemoNoteTemplateSchema).max(200),
    visitTypeDefaults: DemoNoteTemplateDefaultsSchema,
})
    .strict()
    .superRefine((value, context) => {
    const templateIds = value.templates.map((template) => template.id);
    if (new Set(templateIds).size !== templateIds.length) {
        context.addIssue({
            code: "custom",
            message: "Template identifiers must be unique",
            path: ["templates"],
        });
    }
    const templatesById = new Map(value.templates.map((template) => [template.id, template]));
    value.visitTypeDefaults.defaults.forEach((entry, index) => {
        const template = templatesById.get(entry.templateId);
        if (template === undefined) {
            context.addIssue({
                code: "custom",
                message: "Visit type default references an unknown template",
                path: ["visitTypeDefaults", "defaults", index, "templateId"],
            });
        }
        else if (template.category !== "encounter") {
            context.addIssue({
                code: "custom",
                message: "Visit type defaults may reference only encounter templates",
                path: ["visitTypeDefaults", "defaults", index, "templateId"],
            });
        }
    });
});
export const CreateDemoNoteTemplateRequestSchema = z
    .object({
    name: ShortTextSchema,
    description: OptionalTextSchema.default(""),
    category: DemoNoteTemplateCategorySchema.default("encounter"),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const UpdateDemoNoteTemplateRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    template: DemoNoteTemplateSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.template.version !== value.expectedVersion) {
        context.addIssue({
            code: "custom",
            message: "Template version must match the expected version",
            path: ["template", "version"],
        });
    }
});
export const DuplicateDemoNoteTemplateRequestSchema = z
    .object({
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const ArchiveDemoNoteTemplateRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const RestoreDemoNoteTemplateRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const UpdateDemoNoteTemplateDefaultsRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    defaults: z.array(DemoNoteTemplateVisitTypeDefaultSchema).max(100),
})
    .strict()
    .superRefine((value, context) => {
    const appointmentTypeIds = value.defaults.map((entry) => entry.appointmentTypeId);
    if (new Set(appointmentTypeIds).size !== appointmentTypeIds.length) {
        context.addIssue({
            code: "custom",
            message: "Each visit type may map to at most one default template",
            path: ["defaults"],
        });
    }
});
//# sourceMappingURL=demo-note-templates.js.map